/**
 * Minimal zip reader and writer shared by every in-app viewer of a zip-based
 * format — `.xlsx` workbooks, `.pptx` speaker notes and plain `.zip` archives.
 *
 * Only what a well-formed archive needs: the end-of-central-directory record,
 * the central directory entries it points at, and stored/deflated entry
 * bodies. Inflating uses the platform's own `DecompressionStream("deflate-raw")`,
 * so the module runs, and is tested, anywhere without a compression library.
 * Zip64, multi-disk and encrypted entries are out of scope: the directory
 * still lists them, but their bodies are refused with a clear reason.
 *
 * Anything malformed throws {@link ZipParseError}; callers show their format's
 * download surface instead, so an archive we cannot read still reaches the
 * user as bytes.
 */

export class ZipParseError extends Error {
  constructor(message: string) {
    super(message);
    this.name = "ZipParseError";
  }
}

const SIGNATURE_EOCD = 0x06054b50;
const SIGNATURE_CENTRAL_FILE = 0x02014b50;
const SIGNATURE_LOCAL_FILE = 0x04034b50;
/** A 32-bit size or offset field at its maximum means "look in the Zip64 extra field". */
const ZIP64_SENTINEL = 0xffffffff;
const ZIP64_COUNT_SENTINEL = 0xffff;
const FLAG_ENCRYPTED = 0x0001;
const FLAG_UTF8_NAMES = 0x0800;
const DOS_ATTRIBUTE_DIRECTORY = 0x10;
/** Info-ZIP "Unicode Path" extra field: the UTF-8 name for an archive whose header name is legacy-encoded. */
const EXTRA_UNICODE_PATH = 0x7075;
const UNIX_MODE_DIRECTORY = 0o040000;

export const COMPRESSION_STORED = 0;
export const COMPRESSION_DEFLATE = 8;

export interface ZipEntry {
  readonly name: string;
  readonly compressionMethod: number;
  readonly compressedSize: number;
  readonly uncompressedSize: number;
  readonly crc32: number;
  readonly localHeaderOffset: number;
  /** A directory record (trailing slash or a directory attribute); it has no body. */
  readonly directory: boolean;
  /** The body is encrypted; it can be listed but never read here. */
  readonly encrypted: boolean;
  /** MS-DOS modification stamp in the archive's local time, or null when the writer left it zero. */
  readonly modifiedAt: Date | null;
}

const inflateRaw = async (bytes: Uint8Array): Promise<Uint8Array<ArrayBuffer>> => {
  if (typeof DecompressionStream === "undefined") {
    throw new ZipParseError("This browser cannot decompress zip archives.");
  }
  // Blob rejects views over resizable buffers in browsers; copy only this body.
  const stream = new Blob([bytes.slice()]).stream().pipeThrough(new DecompressionStream("deflate-raw"));
  return new Uint8Array(await new Response(stream).arrayBuffer());
};

const findEndOfCentralDirectory = (view: DataView): number => {
  // The record is at the end, but a trailing comment can push it back up to
  // 64 KiB; scan backwards for the signature. A comment may itself contain
  // the signature bytes, so a candidate counts only when its own comment
  // length makes the record end exactly at the end of the file.
  const minimumOffset = Math.max(0, view.byteLength - 0xffff - 22);
  for (let offset = view.byteLength - 22; offset >= minimumOffset; offset -= 1) {
    if (
      view.getUint32(offset, true) === SIGNATURE_EOCD &&
      offset + 22 + view.getUint16(offset + 20, true) === view.byteLength
    ) {
      return offset;
    }
  }
  throw new ZipParseError("Not a valid zip archive (no central directory).");
};

/**
 * Code page 437, the encoding the zip specification prescribes for names
 * without the UTF-8 flag. The low half is ASCII; this is the high half, in
 * byte order from 0x80. `TextDecoder` has no CP437, and its Latin-1/
 * Windows-1252 tables would turn every accented legacy name into a
 * different accented name — a listing that then cannot find its own entry.
 */
const CP437_HIGH =
  "ÇüéâäàåçêëèïîìÄÅÉæÆôöòûùÿÖÜ¢£¥₧ƒáíóúñÑªº¿⌐¬½¼¡«»░▒▓│┤╡╢╖╕╣║╗╝╜╛┐└┴┬├─┼╞╟╚╔╩╦╠═╬╧╨╤╥╙╘╒╓╫╪┘┌█▄▌▐▀αßΓπΣσµτΦΘΩδ∞φε∩≡±≥≤⌠⌡÷≈°∙·√ⁿ²■\u00a0";

export const decodeCp437 = (bytes: Uint8Array): string => {
  let text = "";
  for (const byte of bytes) {
    text += byte < 0x80 ? String.fromCharCode(byte) : CP437_HIGH[byte - 0x80]!;
  }
  return text;
};

/**
 * MS-DOS date/time: the date's bits are year-since-1980 (7), month (4) and
 * day (5); the time's are hours (5), minutes (6) and two-second steps (5).
 * The stamp carries no zone — it is the writer's local time — so it is read
 * as the viewer's local time, which is what every archive tool shows.
 */
export const dosDateTime = (date: number, time: number): Date | null => {
  if (date === 0) return null;
  const year = 1980 + (date >> 9);
  const month = (date >> 5) & 0x0f;
  const day = date & 0x1f;
  // A month of 13 or a 31 February is not a date; `Date` would roll it over
  // into a plausible-looking but wrong day.
  if (month < 1 || month > 12 || day < 1 || day > new Date(year, month, 0).getDate()) return null;
  const stamp = new Date(
    year,
    month - 1,
    day,
    Math.min(23, time >> 11),
    Math.min(59, (time >> 5) & 0x3f),
    Math.min(59, (time & 0x1f) * 2),
  );
  return Number.isNaN(stamp.getTime()) ? null : stamp;
};

/**
 * The name from an Info-ZIP Unicode Path extra field, when present and still
 * describing this header name: the field carries the CRC-32 of the legacy
 * name bytes so a rename by a tool that did not know the field is detected.
 */
const unicodePathFromExtra = (extra: Uint8Array, legacyName: Uint8Array): string | null => {
  const view = new DataView(extra.buffer, extra.byteOffset, extra.byteLength);
  for (let cursor = 0; cursor + 4 <= extra.byteLength; ) {
    const id = view.getUint16(cursor, true);
    const size = view.getUint16(cursor + 2, true);
    const start = cursor + 4;
    if (start + size > extra.byteLength) return null;
    if (id === EXTRA_UNICODE_PATH && size >= 5 && extra[start] === 1) {
      if (view.getUint32(start + 1, true) !== crc32(legacyName)) return null;
      try {
        return new TextDecoder("utf-8", { fatal: true }).decode(extra.slice(start + 5, start + size));
      } catch {
        return null;
      }
    }
    cursor = start + size;
  }
  return null;
};

export const readZipEntries = (bytes: Uint8Array): Map<string, ZipEntry> => {
  const view = new DataView(bytes.buffer, bytes.byteOffset, bytes.byteLength);
  const eocd = findEndOfCentralDirectory(view);
  const entryCount = view.getUint16(eocd + 10, true);
  const directoryOffset = view.getUint32(eocd + 16, true);
  if (
    entryCount === ZIP64_COUNT_SENTINEL ||
    view.getUint32(eocd + 12, true) === ZIP64_SENTINEL ||
    directoryOffset === ZIP64_SENTINEL
  ) {
    throw new ZipParseError("Zip64 archives are not supported.");
  }
  // A split archive numbers its disks; every offset here is relative to a
  // part we do not have, so the whole archive is refused up front rather
  // than listed and then failing entry by entry.
  if (view.getUint16(eocd + 4, true) !== 0 || view.getUint16(eocd + 6, true) !== 0) {
    throw new ZipParseError("Multi-part zip archives are not supported.");
  }
  let cursor = directoryOffset;
  const utf8 = new TextDecoder("utf-8", { fatal: true });
  const entries = new Map<string, ZipEntry>();

  for (let index = 0; index < entryCount; index += 1) {
    if (cursor + 46 > bytes.byteLength || view.getUint32(cursor, true) !== SIGNATURE_CENTRAL_FILE) {
      throw new ZipParseError("The zip archive's directory is corrupt.");
    }
    if (view.getUint16(cursor + 34, true) !== 0) {
      throw new ZipParseError("Multi-part zip archives are not supported.");
    }
    const madeByHost = view.getUint8(cursor + 5);
    const flags = view.getUint16(cursor + 8, true);
    const compressedSize = view.getUint32(cursor + 20, true);
    const uncompressedSize = view.getUint32(cursor + 24, true);
    const localHeaderOffset = view.getUint32(cursor + 42, true);
    if (
      compressedSize === ZIP64_SENTINEL ||
      uncompressedSize === ZIP64_SENTINEL ||
      localHeaderOffset === ZIP64_SENTINEL
    ) {
      throw new ZipParseError("Zip64 archives are not supported.");
    }
    const nameLength = view.getUint16(cursor + 28, true);
    const extraLength = view.getUint16(cursor + 30, true);
    const commentLength = view.getUint16(cursor + 32, true);
    const externalAttributes = view.getUint32(cursor + 38, true);
    if (cursor + 46 + nameLength + extraLength + commentLength > bytes.byteLength) {
      throw new ZipParseError("The zip archive's directory is corrupt.");
    }
    // TextDecoder rejects resizable views in browsers; copy only the filename.
    const nameBytes = bytes.slice(cursor + 46, cursor + 46 + nameLength);
    const name =
      flags & FLAG_UTF8_NAMES
        ? utf8.decode(nameBytes)
        : (unicodePathFromExtra(
            bytes.subarray(cursor + 46 + nameLength, cursor + 46 + nameLength + extraLength),
            nameBytes,
          ) ?? decodeCp437(nameBytes));
    const directory =
      name.endsWith("/") ||
      (externalAttributes & DOS_ATTRIBUTE_DIRECTORY) !== 0 ||
      (madeByHost === 3 && ((externalAttributes >>> 16) & 0o170000) === UNIX_MODE_DIRECTORY);
    if (entries.has(name)) throw new ZipParseError("The zip archive lists an entry twice.");
    entries.set(name, {
      name,
      compressionMethod: view.getUint16(cursor + 10, true),
      compressedSize,
      uncompressedSize,
      crc32: view.getUint32(cursor + 16, true),
      localHeaderOffset,
      directory,
      encrypted: (flags & FLAG_ENCRYPTED) !== 0,
      modifiedAt: dosDateTime(view.getUint16(cursor + 14, true), view.getUint16(cursor + 12, true)),
    });
    cursor += 46 + nameLength + extraLength + commentLength;
  }
  return entries;
};

/** The entry's stored body, still compressed — what a rewrite copies through. */
export const readZipEntryBody = (bytes: Uint8Array, entry: ZipEntry): Uint8Array => {
  if (entry.encrypted) throw new ZipParseError("This entry is encrypted.");
  const view = new DataView(bytes.buffer, bytes.byteOffset, bytes.byteLength);
  if (entry.localHeaderOffset + 30 > bytes.byteLength) {
    throw new ZipParseError("The zip archive is truncated.");
  }
  // A directory record whose offset lands anywhere but on this entry's own
  // local header (corruption, or a tampered offset) must not hand back some
  // other entry's bytes under this entry's name.
  if (view.getUint32(entry.localHeaderOffset, true) !== SIGNATURE_LOCAL_FILE) {
    throw new ZipParseError("The zip archive's directory does not match its entries.");
  }
  // The local header repeats the name/extra lengths, which are the only
  // reliable way to find where the body starts.
  const nameLength = view.getUint16(entry.localHeaderOffset + 26, true);
  const extraLength = view.getUint16(entry.localHeaderOffset + 28, true);
  const start = entry.localHeaderOffset + 30 + nameLength + extraLength;
  if (start + entry.compressedSize > bytes.byteLength) {
    throw new ZipParseError("The zip archive is truncated.");
  }
  return bytes.subarray(start, start + entry.compressedSize);
};

export interface ZipReadOptions {
  /** Refuse an entry whose declared or actual inflated size exceeds this. */
  readonly maxBytes: number;
  readonly signal?: AbortSignal | undefined;
}

const assertReadable = (entry: ZipEntry): void => {
  if (entry.encrypted) throw new ZipParseError("This entry is encrypted.");
  if (entry.compressionMethod !== COMPRESSION_STORED && entry.compressionMethod !== COMPRESSION_DEFLATE) {
    throw new ZipParseError("Unsupported archive compression.");
  }
};

const checked = (entry: ZipEntry, output: Uint8Array<ArrayBuffer>): Uint8Array<ArrayBuffer> => {
  if (output.byteLength !== entry.uncompressedSize || crc32(output) !== entry.crc32) {
    throw new ZipParseError("The entry's bytes do not match its directory record.");
  }
  return output;
};

/**
 * The entry's inflated bytes, verified against the directory's size and
 * CRC-32 so a corrupt or tampered archive never shows or saves the wrong
 * bytes under an entry's name. With options, the read is bounded on the way
 * through: the declared size is checked first, then the actual inflated
 * length, so an archive whose metadata understates a part cannot expand past
 * the budget in memory. The output is written straight into one buffer of
 * the declared size — the chunks are never retained alongside it.
 */
export const readZipEntryBytes = async (
  bytes: Uint8Array,
  entry: ZipEntry,
  options?: ZipReadOptions,
): Promise<Uint8Array<ArrayBuffer>> => {
  assertReadable(entry);
  const body = readZipEntryBody(bytes, entry);
  if (!options) {
    return checked(
      entry,
      entry.compressionMethod === COMPRESSION_STORED ? body.slice() : await inflateRaw(body),
    );
  }
  options.signal?.throwIfAborted();
  const overLimit = () => new ZipParseError("Archive entry exceeds the preview limit.");
  if (entry.uncompressedSize > options.maxBytes || body.byteLength > options.maxBytes + 65_536) {
    throw overLimit();
  }
  // Blob rejects views over resizable buffers in browsers. Copy only this
  // bounded compressed part, never the complete archive download.
  let stream = new Blob([body.slice()]).stream();
  if (entry.compressionMethod === COMPRESSION_DEFLATE) {
    if (typeof DecompressionStream === "undefined") {
      throw new ZipParseError("This browser cannot decompress zip archives.");
    }
    stream = stream.pipeThrough(new DecompressionStream("deflate-raw"));
  }
  const reader = stream.getReader();
  const output = new Uint8Array(entry.uncompressedSize);
  let length = 0;
  try {
    while (true) {
      options.signal?.throwIfAborted();
      const { done, value } = await reader.read();
      if (done) break;
      length += value.byteLength;
      if (length > options.maxBytes) throw overLimit();
      if (length > output.byteLength) {
        throw new ZipParseError("The entry's bytes do not match its directory record.");
      }
      output.set(value, length - value.byteLength);
    }
  } finally {
    await reader.cancel();
    reader.releaseLock();
  }
  if (length !== output.byteLength) {
    throw new ZipParseError("The entry's bytes do not match its directory record.");
  }
  return checked(entry, output);
};

export const readZipEntryText = async (
  bytes: Uint8Array,
  entry: ZipEntry,
  options?: ZipReadOptions,
): Promise<string> => new TextDecoder("utf-8").decode(await readZipEntryBytes(bytes, entry, options));

// ── writing ────────────────────────────────────────────────────────────────

const CRC_TABLE = (() => {
  const table = new Uint32Array(256);
  for (let n = 0; n < 256; n += 1) {
    let c = n;
    for (let k = 0; k < 8; k += 1) c = c & 1 ? 0xedb88320 ^ (c >>> 1) : c >>> 1;
    table[n] = c >>> 0;
  }
  return table;
})();

export const crc32 = (bytes: Uint8Array): number => {
  let crc = 0xffffffff;
  for (const byte of bytes) crc = CRC_TABLE[(crc ^ byte) & 0xff]! ^ (crc >>> 8);
  return (crc ^ 0xffffffff) >>> 0;
};

export interface ZipOutputEntry {
  readonly name: string;
  readonly body: Uint8Array;
  readonly compressionMethod: number;
  readonly crc32: number;
  readonly uncompressedSize: number;
}

/** A well-formed single-part zip, names flagged UTF-8, from entries whose bodies are already in stored form. */
export const writeZip = (entries: ReadonlyArray<ZipOutputEntry>): Uint8Array<ArrayBuffer> => {
  // The end record counts entries in 16 bits (0xffff itself being the Zip64
  // sentinel) and addresses the directory in 32; past either the archive
  // would need Zip64, which this writer does not produce — so refuse rather
  // than emit a truncated count a reader takes as an empty or corrupt archive.
  if (entries.length >= ZIP64_COUNT_SENTINEL) {
    throw new ZipParseError("Zip64 archives are not supported.");
  }
  const encoder = new TextEncoder();
  const locals: Uint8Array[] = [];
  const centrals: Uint8Array[] = [];
  let offset = 0;
  const names = new Set<string>();
  for (const entry of entries) {
    if (names.has(entry.name)) throw new ZipParseError("The zip archive lists an entry twice.");
    names.add(entry.name);
    const nameBytes = encoder.encode(entry.name);
    if (nameBytes.length > 0xffff) throw new ZipParseError("Zip entry name is too long.");
    const local = new Uint8Array(30 + nameBytes.length + entry.body.length);
    const lv = new DataView(local.buffer);
    lv.setUint32(0, 0x04034b50, true);
    lv.setUint16(4, 20, true);
    // Names are written as UTF-8, and the flag says so; without it a reader
    // must take them as CP437.
    lv.setUint16(6, FLAG_UTF8_NAMES, true);
    lv.setUint16(8, entry.compressionMethod, true);
    lv.setUint32(14, entry.crc32, true);
    lv.setUint32(18, entry.body.length, true);
    lv.setUint32(22, entry.uncompressedSize, true);
    lv.setUint16(26, nameBytes.length, true);
    local.set(nameBytes, 30);
    local.set(entry.body, 30 + nameBytes.length);
    locals.push(local);
    const central = new Uint8Array(46 + nameBytes.length);
    const cv = new DataView(central.buffer);
    cv.setUint32(0, SIGNATURE_CENTRAL_FILE, true);
    cv.setUint16(4, 20, true);
    cv.setUint16(6, 20, true);
    cv.setUint16(8, FLAG_UTF8_NAMES, true);
    cv.setUint16(10, entry.compressionMethod, true);
    cv.setUint32(16, entry.crc32, true);
    cv.setUint32(20, entry.body.length, true);
    cv.setUint32(24, entry.uncompressedSize, true);
    cv.setUint16(28, nameBytes.length, true);
    cv.setUint32(42, offset, true);
    central.set(nameBytes, 46);
    centrals.push(central);
    offset += local.length;
  }
  const centralSize = centrals.reduce((total, part) => total + part.length, 0);
  if (offset + centralSize > 0xffffffff) throw new ZipParseError("Zip64 archives are not supported.");
  const end = new Uint8Array(22);
  const ev = new DataView(end.buffer);
  ev.setUint32(0, SIGNATURE_EOCD, true);
  ev.setUint16(8, entries.length, true);
  ev.setUint16(10, entries.length, true);
  ev.setUint32(12, centralSize, true);
  ev.setUint32(16, offset, true);
  const output = new Uint8Array(offset + centralSize + 22);
  let cursor = 0;
  for (const part of [...locals, ...centrals, end]) {
    output.set(part, cursor);
    cursor += part.length;
  }
  return output;
};
